<template>
    <v-container fluid class="ma-0 pa-0 login-bg">
        <loginSVG v-bind:class="[isMobile ? 'loginSVG-mobile' : 'loginSVG']"
                  :colors="loginSVGColors"
                  role="presentation"
                  aria-hidden="true">
        </loginSVG>
        <v-row>
            <v-toolbar flat
                       :color="loginHeaderBackgroundColor ? 'loginHeaderBackgroundColor' : ''"
                       role="banner">
                <v-hover v-slot="{hover}">
                    <v-btn @click="toggleLocale"
                           class="text-none text-normal min-height-1"
                           text absolute right plain
                           :color="hover ? 'linkHover' : loginHeaderFontColor ? 'loginHeaderFontColor' : 'primary'"
                           :aria-label="$t('accessibility.toggleLanguage')">
                        {{$t('login.locale')}}
                    </v-btn>
                </v-hover>
            </v-toolbar>
        </v-row>
        <v-row class="mt-5 ml-4">
            <v-img class=""
                   :src="`../../assets/Branding/${this.$experience.key}/Images/${getSecondLogo}`"
                   :max-width="logoSecondLoginMaxWidth ? logoSecondLoginMaxWidth : '270px'"
                   :max-height="logoSecondLoginMaxheight ? logoSecondLoginMaxheight : null"
                   :alt="$t('app.logoAlt')"
                   contain>
            </v-img>
        </v-row>
        <v-row align="center" justify="center" :class="isMobile ? [] : ['py-16', 'mt-16']">
            <v-col align="center">
                <v-card class="my-auto rounded-xl"
                        v-bind:class="isMobile ? ['pt-4', 'px-2'] : ['pt-4', 'px-4']"
                        max-width="500px"
                        flat
                        role="main"
                        :aria-labelledby="loginTitleId">
                    <v-row class="mt-5">
                        <v-img class="mx-auto login-logo"
                               :src="`../../assets/Branding/${this.$experience.key}/Images/${getLogo}`"
                               :max-width="loginLogoMaxWidth ? loginLogoMaxWidth : '270px'"
                               :max-height="loginLogoMaxheight ? loginLogoMaxheight : null"
                               :alt="$t('app.logoAlt')"
                               contain>
                        </v-img>
                    </v-row>
                    <v-card-subtitle 
                        class="text-center card-title" 
                        :id="loginTitleId"
                        role="heading"
                        aria-level="1">
                        {{$t('login.title')}}
                    </v-card-subtitle>

                    <!-- Error Alert with ARIA -->
                    <v-alert 
                        v-if="alert.message" 
                        class="mx-4 pa-3 d-inline-block" 
                        dense 
                        dark 
                        type="error"
                        role="alert"
                        aria-atomic="true">
                        {{$t(alert.message)}}
                    </v-alert>

                    <v-form 
                        @submit.prevent="handleSubmit"
                        :aria-labelledby="loginTitleId"
                        novalidate>
                        <v-card-text class="py-0 mt-2">
                            <!-- Username Field -->
                            <label :for="usernameId" class="sr-only">{{$t('username')}}</label>
                            <v-text-field 
                                v-model="username"
                                :id="usernameId"
                                :placeholder="$t('username')"
                                :label="$t('username')"
                                outlined
                                hide-details="auto"
                                maxlength="50"
                                class="mb-4 text-normal"
                                prepend-inner-icon="mdi-account-outline"
                                :aria-required="true"
                                :aria-invalid="usernameError ? 'true' : 'false'"
                                :aria-describedby="usernameError ? usernameErrorId : null"
                                autocomplete="username"
                                @blur="validateUsername"
                                @input="clearUsernameError">
                                <template v-slot:prepend-inner>
                                    <v-icon aria-hidden="true">mdi-account-outline</v-icon>
                                </template>
                            </v-text-field>
                            <span 
                                v-if="usernameError" 
                                :id="usernameErrorId"
                                class="error-message"
                                role="alert">
                                {{usernameError}}
                            </span>

                            <!-- Password Field -->
                            <label :for="passwordId" class="sr-only">{{$t('password')}}</label>
                            <v-text-field 
                                v-model="password"
                                :id="passwordId"
                                :placeholder="$t('password')"
                                :label="$t('password')"
                                :type="passwordVisible ? 'text' : 'password'"
                                outlined
                                hide-details="auto"
                                maxlength="30"
                                class="mb-4 text-normal"
                                :aria-required="true"
                                :aria-invalid="passwordError ? 'true' : 'false'"
                                :aria-describedby="passwordError ? passwordErrorId : null"
                                autocomplete="current-password"
                                @blur="validatePassword"
                                @input="clearPasswordError">
                                <template v-slot:prepend-inner>
                                    <v-icon aria-hidden="true">mdi-lock-outline</v-icon>
                                </template>
                                <template v-slot:append>
                                    <v-btn
                                        icon
                                        tabindex="0"
                                        @click="togglePasswordVisibility"
                                        :aria-label="passwordVisible ? $t('accessibility.hidePassword') : $t('accessibility.showPassword')"
                                        :aria-pressed="passwordVisible ? 'true' : 'false'">
                                        <v-icon aria-hidden="true">
                                            {{passwordVisible ? 'mdi-eye-off-outline' : 'mdi-eye-outline'}}
                                        </v-icon>
                                    </v-btn>
                                </template>
                            </v-text-field>
                            <span 
                                v-if="passwordError" 
                                :id="passwordErrorId"
                                class="error-message"
                                role="alert">
                                {{passwordError}}
                            </span>

                            <v-row>
                                <v-hover v-slot="{hover}">
                                    <v-btn 
                                        class="text-none text-small min-height-1" 
                                        to="/forgotPassword" 
                                        text 
                                        plain 
                                        :color="hover ? (loginLinkHoverColor ? loginLinkHoverColor : 'linkHover') : (loginLinkColor ? loginLinkColor : 'primary')">
                                        {{$t('login.forgotPassword')}}
                                    </v-btn>
                                </v-hover>
                            </v-row>
                        </v-card-text>
                        <v-card-actions>
                            <v-col class="mx-2">
                                <v-row>
                                    <v-hover v-slot="{hover}">
                                        <v-btn 
                                            class="text-none text-normal min-height-1"
                                            depressed
                                            block
                                            :disabled="isFormProcessing"
                                            :dark="!isFormProcessing"
                                            :color="hover ? 'buttonHover' : 'primary'"
                                            type="submit"
                                            :aria-busy="isFormProcessing ? 'true' : 'false'"
                                            :aria-label="isFormProcessing ? $t('accessibility.loading') : $t('login.login')">
                                            {{$t('login.login')}} &nbsp;
                                            <v-progress-circular 
                                                v-if="isFormProcessing"
                                                indeterminate
                                                size="20"
                                                color="primary"
                                                aria-hidden="true" />
                                            <span v-if="isFormProcessing" class="sr-only">
                                                {{$t('accessibility.loading')}}
                                            </span>
                                        </v-btn>
                                    </v-hover>
                                </v-row>
                                <v-row class="mb-2">
                                    <v-hover v-slot="{hover}">
                                        <v-btn 
                                            v-if="displayCreateAccountLink"
                                            class="text-normal min-height-1 font-weigth-medium"
                                            to="/register" 
                                            block 
                                            text 
                                            plain
                                            :color="hover ? (loginLinkHoverColor ? loginLinkHoverColor : 'linkHover') : (loginLinkColor ? loginLinkColor : 'primary')">
                                            {{$t('login.createAccount')}}
                                        </v-btn>
                                    </v-hover>
                                </v-row>
                            </v-col>
                        </v-card-actions>
                    </v-form>
                </v-card>
            </v-col>
        </v-row>
    </v-container>
</template>

<script>
    import { mapState, mapActions } from "vuex";
    import loginSVG from '../../components/loginSVG.vue';
    import { generateId } from '../../utils/accessibility';

    export default {
        components: { loginSVG },
        data() {
            return {
                username: "",
                password: "",
                hasonboarding: false,
                passwordVisible: false,
                formProcess: false,
                logo: "",
                rejectPassword: 0,
                lockedOut: null,
                usernameError: "",
                passwordError: "",
                // Generate unique IDs for accessibility
                usernameId: generateId('username'),
                passwordId: generateId('password'),
                usernameErrorId: generateId('username-error'),
                passwordErrorId: generateId('password-error'),
                loginTitleId: generateId('login-title')
            };
        },
        computed: {
            ...mapState("account", ["status", "user"]),
            ...mapState({ alert: state => state.alert }),

            displayCreateAccountLink: function () {
                return this.$hasonboarding ? this.$hasonboarding : false;
            },
            isFormProcessing() {
                return this.formProcess && !this.alert.message;
            },
            isMobile() {
                return this.$vuetify.breakpoint.smAndDown;
            },

            loginSVGColors() {
                return this.$vuetify.theme.themes.light.loginSVG;
            },

            loginLogoMaxWidth() {
                return this.$experience.loginLogoMaxWidth;
            },
            loginLogoMaxheight() {
                return this.$experience.loginLogoMaxheight;
            },
            loginHeaderBackgroundColor() {
                return this.$vuetify.theme.themes.light.loginHeaderBackgroundColor;
            },
            loginHeaderFontColor() {
                return this.$vuetify.theme.themes.light.loginHeaderFontColor;
            },
            logoSecondLoginMaxWidth() {
                return this.$experience.logoSecondLoginMaxWidth;
            },
            logoSecondLoginMaxheight() {
                return this.$experience.logoSecondLoginMaxheight;
            },
            loginLinkHoverColor() {
                return this.$experience.loginLinkHoverColor;
            },

            loginLinkColor() {
                return this.$experience.loginLinkColor;
            },
            getLogo() {
                return (this.$i18n.locale == 'en' && this.$experience.logoLoginEN != null) ? this.$experience.logoLoginEN : this.$experience.logo;
            },
            getSecondLogo() {
                return (this.$i18n.locale == 'en' && this.$experience.logoSecondLoginEN != null) ? this.$experience.logoSecondLoginEN : this.$experience.logoSecondLogin;
            }
        },
        created() {
            // reset login status
            this.logout();
            this.displayLogin();
        },
        mounted() {
            let unsubscribe = null
            unsubscribe = this.$store.subscribe(({ type }) => {
                if (type === 'account/loginSuccess') {
                    unsubscribe() // So it only reacts once.
                }
            })
        },
        methods: {
            ...mapActions("account", ["login", "logout"]),
            ...mapActions({ clearAlert: 'alert/clear' }),
            ...mapActions({ errorAlert: 'alert/error' }),

            validateUsername() {
                if (!this.username || this.username.trim() === '') {
                    this.usernameError = this.$t('login.username-required');
                    return false;
                }
                this.usernameError = '';
                return true;
            },

            validatePassword() {
                if (!this.password || this.password.trim() === '') {
                    this.passwordError = this.$t('login.password-required');
                    return false;
                }
                this.passwordError = '';
                return true;
            },

            clearUsernameError() {
                if (this.usernameError) {
                    this.usernameError = '';
                }
            },

            clearPasswordError() {
                if (this.passwordError) {
                    this.passwordError = '';
                }
            },

            togglePasswordVisibility() {
                this.passwordVisible = !this.passwordVisible;
                // Announce to screen readers
                const message = this.passwordVisible 
                    ? this.$t('accessibility.showPassword')
                    : this.$t('accessibility.hidePassword');
                this.$announce(message, 'polite');
            },

            handleSubmit() {
                // Validate form
                const isUsernameValid = this.validateUsername();
                const isPasswordValid = this.validatePassword();

                if (!isUsernameValid || !isPasswordValid) {
                    // Announce form errors to screen readers
                    this.$announcer.assertive(this.$t('accessibility.formError'));

                    // Focus first error field
                    this.$nextTick(() => {
                        if (!isUsernameValid) {
                            document.getElementById(this.usernameId)?.focus();
                        } else if (!isPasswordValid) {
                            document.getElementById(this.passwordId)?.focus();
                        }
                    });
                    return;
                }

                this.formProcess = true;
                this.clearAlert();
                const { username, password } = this;

                if (username && password) {
                    // Announce loading state
                    this.$announcer.polite(this.$t('accessibility.loading'));
                    this.login({ username, password });
                } else {
                    this.formProcess = false;
                    const errorMsg = !username ? 'login.username-required' : 'login.password-required';
                    this.errorAlert(errorMsg);
                    // Announce error to screen readers
                    this.$announcer.assertive(this.$t(errorMsg));
                }
            },

            displayLogin() {
                this.clearAlert();
            },

            toggleLocale() {
                this.$emit("toggleLocale");
            }
        },
        watch: {
            $route() {
                // clear alert on location change
                this.clearAlert();
            },
            'alert.message'(newVal) {
                // Announce errors to screen readers
                if (newVal) {
                    this.$announcer.assertive(this.$t(newVal));
                    this.formProcess = false;
                }
            }
        }
    };
</script>

<style scoped>

    .login-logo {
        margin-top: 50px !important;
        margin-bottom: 50px !important;
    }

    .loginSVG {
        position: absolute;
        top: 50%;
        width: 100%;
        height: 50%;
    }

    .loginSVG-mobile {
        position: absolute;
        top: 48px;
        width: 100%;
        height: 50%;
    }

    .error-message {
        display: block;
        color: #d32f2f;
        font-size: 0.75rem;
        margin-top: -12px;
        margin-bottom: 12px;
        margin-left: 12px;
    }

    /* Ensure labels are accessible even though using placeholders */
    label.sr-only {
        position: absolute;
        left: -10000px;
        width: 1px;
        height: 1px;
        overflow: hidden;
    }
</style>

